const AUTO_SCROLL_SPEED_PX_PER_SECOND = 45;
const RESUME_DELAY_MS = 1000;
const AUTO_SCROLL_EVENT_IGNORE_MS = 120;

export function initAutoScroll(options = {}) {
	const speed = Number(options.speedPxPerSecond ?? AUTO_SCROLL_SPEED_PX_PER_SECOND);
	const resumeDelayMs = Number(options.resumeDelayMs ?? RESUME_DELAY_MS);
	const scroller = options.scroller || document.scrollingElement || document.documentElement;
	const isWindowScroller =
		scroller === document.documentElement || scroller === document.body;

	if (!Number.isFinite(speed) || speed <= 0) return;

	let rafId = null;
	let lastFrameTime = null;
	let targetScrollTop = 0;
	let isRunning = false;
	let lastAutoScrollAt = 0;
	let resumeTimer = null;
	let startRetryTimer = null;

	function getScrollTop() {
		return isWindowScroller ? window.scrollY : scroller.scrollTop;
	}

	function getViewportHeight() {
		return isWindowScroller ? window.innerHeight : scroller.clientHeight;
	}

	function getScrollHeight() {
		return isWindowScroller ? document.documentElement.scrollHeight : scroller.scrollHeight;
	}

	function setScrollTop(value) {
		if (isWindowScroller) {
			window.scrollTo(0, value);
			return;
		}
		scroller.scrollTop = value;
	}

	function canScrollDown() {
		return getScrollTop() + getViewportHeight() < getScrollHeight();
	}

	function stopAutoScroll() {
		isRunning = false;
		lastFrameTime = null;
		if (rafId !== null) {
			cancelAnimationFrame(rafId);
			rafId = null;
		}
	}

	function tick(timestamp) {
		if (!isRunning) return;

		if (lastFrameTime === null) {
			lastFrameTime = timestamp;
			rafId = requestAnimationFrame(tick);
			return;
		}

		const deltaSeconds = (timestamp - lastFrameTime) / 1000;
		lastFrameTime = timestamp;

		if (!canScrollDown()) {
			stopAutoScroll();
			return;
		}

		targetScrollTop += speed * deltaSeconds;
		lastAutoScrollAt = performance.now();
		setScrollTop(targetScrollTop);

		rafId = requestAnimationFrame(tick);
	}

	function startAutoScroll() {
		if (isRunning || !canScrollDown()) return;
		targetScrollTop = getScrollTop();
		isRunning = true;
		rafId = requestAnimationFrame(tick);
	}

	function ensureAutoScrollStarts() {
		startAutoScroll();
		if (isRunning || startRetryTimer !== null) return;
		startRetryTimer = window.setTimeout(() => {
			startRetryTimer = null;
			startAutoScroll();
		}, 250);
	}

	function scheduleResume() {
		stopAutoScroll();
		if (resumeTimer !== null) {
			clearTimeout(resumeTimer);
		}
		resumeTimer = window.setTimeout(() => {
			resumeTimer = null;
			startAutoScroll();
		}, resumeDelayMs);
	}

	if (isWindowScroller) {
		window.addEventListener(
			'scroll',
			() => {
				if (performance.now() - lastAutoScrollAt < AUTO_SCROLL_EVENT_IGNORE_MS) return;
				targetScrollTop = getScrollTop();
				scheduleResume();
			},
			{ passive: true }
		);
	} else {
		scroller.addEventListener(
			'scroll',
			() => {
				if (performance.now() - lastAutoScrollAt < AUTO_SCROLL_EVENT_IGNORE_MS) return;
				targetScrollTop = getScrollTop();
				scheduleResume();
			},
			{ passive: true }
		);
	}

	['wheel', 'touchstart', 'keydown', 'mousedown'].forEach((eventName) => {
		window.addEventListener(eventName, scheduleResume, { passive: true });
	});

	window.addEventListener('load', ensureAutoScrollStarts, { passive: true });
	window.addEventListener('resize', ensureAutoScrollStarts, { passive: true });

	if (document.body) {
		const observer = new MutationObserver(() => {
			if (!isRunning) {
				ensureAutoScrollStarts();
			}
		});
		observer.observe(document.body, { childList: true, subtree: true });
	}

	ensureAutoScrollStarts();
}


